import type {
  ClaimStatus,
  CorrectiveActionStatus,
  DeliveryInspectionResult,
  InspectionStatus,
  NcrStatus,
  PurchaseOrderStatus,
  RfqMethod,
  RfqStatus,
  SafetyRiskLevel,
  SiteStatus,
  SubcontractStatus,
  TestVerdict,
} from '@/lib/types';

/*
  상태값 → 사람이 읽는 말. **한 곳에만 둔다** — 화면마다 다시 적으면 같은 상태가 화면마다
  다른 이름으로 불린다.
*/
const INSPECTION: Record<InspectionStatus, { label: string; tone: string }> = {
  REQUESTED: { label: '요청', tone: 'badge-blue' },
  IN_REVIEW: { label: '검측 중', tone: 'badge-blue' },
  PASSED: { label: '합격', tone: 'badge-gray' },
  FAILED: { label: '불합격', tone: 'badge-red' },
};

const CLAIM: Record<ClaimStatus, { label: string; tone: string }> = {
  CLAIMED: { label: '제출', tone: 'badge-blue' },
  APPROVED: { label: '검수 완료', tone: 'badge-blue' },
  RECEIVED: { label: '지급 완료', tone: 'badge-gray' },
};

const SUBCONTRACT: Record<SubcontractStatus, { label: string; tone: string }> = {
  DRAFT: { label: '검토 중', tone: 'badge-gray' },
  SIGNED: { label: '계약', tone: 'badge-blue' },
  CLOSED: { label: '종료', tone: 'badge-gray' },
};

/*
  현장 상태. 셋 다 사고가 아니라 **단계**라 붉은색을 쓰지 않는다 — 「미진행」은 아직 시작하지
  않은 현장이지 잘못된 현장이 아니다.
*/
const SITE: Record<SiteStatus, string> = {
  진행: 'badge-blue',
  미진행: 'badge-gray',
  준공: 'badge-gray',
};

export function SiteStatusBadge({ status }: { status: SiteStatus }) {
  return <span className={`badge ${SITE[status]}`}>{status}</span>;
}

export function InspectionBadge({ status }: { status: InspectionStatus }) {
  const it = INSPECTION[status];
  return <span className={`badge ${it.tone}`}>{it.label}</span>;
}

export function ClaimBadge({ status }: { status: ClaimStatus }) {
  const it = CLAIM[status];
  return <span className={`badge ${it.tone}`}>{it.label}</span>;
}

export function SubcontractBadge({ status }: { status: SubcontractStatus }) {
  const it = SUBCONTRACT[status];
  return <span className={`badge ${it.tone}`}>{it.label}</span>;
}

/*
  2차·3차 상태. 값 자체가 이미 한국말이라(`types.ts`) 라벨은 띄어쓰기만 다듬는다.
  그래도 여기를 거치게 하는 이유는 <b>색</b>이다 — 같은 상태가 화면마다 다른 색으로 뜨면
  붉은색이 「급한 것」이라는 약속이 깨진다.
*/
const RISK: Record<SafetyRiskLevel, string> = {
  중대: 'badge-red',
  주의: 'badge-blue',
  경미: 'badge-gray',
};

const CORRECTIVE: Record<CorrectiveActionStatus, { label: string; tone: string }> = {
  지적: { label: '조치 필요', tone: 'badge-red' },
  조치완료: { label: '조치 완료', tone: 'badge-blue' },
  확인완료: { label: '확인 완료', tone: 'badge-gray' },
};

const NCR: Record<NcrStatus, { label: string; tone: string }> = {
  발행: { label: '발행', tone: 'badge-red' },
  조치제출: { label: '조치 제출', tone: 'badge-blue' },
  조치확인: { label: '조치 확인', tone: 'badge-blue' },
  종결: { label: '종결', tone: 'badge-gray' },
};

const VERDICT: Record<TestVerdict, string> = {
  판정대기: 'badge-blue',
  합격: 'badge-gray',
  불합격: 'badge-red',
};

const ORDER: Record<PurchaseOrderStatus, { label: string; tone: string }> = {
  발주: { label: '납품 대기', tone: 'badge-blue' },
  부분납품: { label: '부분 납품', tone: 'badge-blue' },
  납품완료: { label: '납품 완료', tone: 'badge-blue' },
  검수완료: { label: '검수 완료', tone: 'badge-gray' },
};

const DELIVERY: Record<DeliveryInspectionResult, { label: string; tone: string }> = {
  검수대기: { label: '검수 대기', tone: 'badge-blue' },
  합격: { label: '합격', tone: 'badge-gray' },
  불합격: { label: '불합격', tone: 'badge-red' },
};

export function RiskBadge({ level }: { level: SafetyRiskLevel }) {
  return <span className={`badge ${RISK[level]}`}>{level}</span>;
}

export function CorrectiveActionBadge({ status }: { status: CorrectiveActionStatus }) {
  const it = CORRECTIVE[status];
  return <span className={`badge ${it.tone}`}>{it.label}</span>;
}

export function NcrBadge({ status }: { status: NcrStatus }) {
  const it = NCR[status];
  return <span className={`badge ${it.tone}`}>{it.label}</span>;
}

export function TestVerdictBadge({ verdict }: { verdict: TestVerdict }) {
  return <span className={`badge ${VERDICT[verdict]}`}>{verdict === '판정대기' ? '판정 대기' : verdict}</span>;
}

/*
  견적·입찰(DOC-98). 상태에 `CLOSED` 가 없다 — 마감은 상태가 아니라 시각으로 판정한 값이라
  **서버가 준 `closed`** 와 함께 읽어야 한 알약이 나온다. 그래서 인자가 둘이다.
*/
const RFQ: Record<RfqStatus, { label: string; tone: string }> = {
  OPEN: { label: '공고 중', tone: 'badge-blue' },
  OPENED: { label: '개찰', tone: 'badge-gray' },
  AWARDED: { label: '낙찰', tone: 'badge-gray' },
};

export function RfqBadge({ status, closed }: { status: RfqStatus; closed: boolean }) {
  if (status === 'OPEN' && closed) return <span className="badge badge-gray">마감</span>;
  const it = RFQ[status];
  return <span className={`badge ${it.tone}`}>{it.label}</span>;
}

/** 방식. 붉은색을 쓰지 않는다 — 견적이든 입찰이든 사고가 아니라 갈래다. */
export function RfqMethodBadge({ method }: { method: RfqMethod }) {
  return <span className="badge badge-gray">{method === 'BID' ? '입찰' : '견적'}</span>;
}

export function PurchaseOrderBadge({ status }: { status: PurchaseOrderStatus }) {
  const it = ORDER[status];
  return <span className={`badge ${it.tone}`}>{it.label}</span>;
}

export function DeliveryBadge({ result }: { result: DeliveryInspectionResult }) {
  const it = DELIVERY[result];
  return <span className={`badge ${it.tone}`}>{it.label}</span>;
}
